/*
 * GeneralTestsNav — the Categories menu in the Header.
 *
 * Every row navigates to that filter's page. How a row shows its children
 * depends on the pointer:
 *  - mouse: they open in a flyout beside the row on hover and close when the
 *    pointer moves on, so nothing in the list shifts under it;
 *  - touch: the chevron expands them in place, accordion style.
 * Rendering is recursive, so a deeper tree needs no render changes.
 *
 * The tree is built from the API (quiz_category.menu_group), never from a list
 * in here — the Flutter app renders the same menu and would drift from a
 * hardcoded copy.
 */

import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { NavDropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import classnames from 'classnames';

import { Icon } from '@/components';
import useHoverMenu, { canHover } from '@/hooks/useHoverMenu';
import { listPublicCategories, CategoryInfo } from '@/services/client/quiz';

import './index.scss';

interface MenuNode {
  key: string;
  label: string;
  /** Query for /public-tests. Every row navigates. */
  params: Record<string, string>;
  children: MenuNode[];
  /** Sum of question_count across every category under this node. */
  questionCount?: number;
}

/** Fixed 2-letter badges for the groups we actually have; anything new
    falls back to the first two letters of the name. */
const GROUP_BADGE: Record<string, string> = {
  US: 'US',
  Canada: 'CA',
  Brazil: 'BR',
  'Tech certifications': 'TC',
};
const badgeFor = (label: string) =>
  GROUP_BADGE[label] || label.slice(0, 2).toUpperCase();

/** Up to two child labels, comma-joined, for a one-line group summary. */
const subtitleFor = (node: MenuNode) =>
  node.children
    .slice(0, 2)
    .map((c) => c.label)
    .join(', ');

/** A node's key plus every descendant key. */
const subtreeKeys = (node: MenuNode, into: string[] = []): string[] => {
  into.push(node.key);
  node.children.forEach((child) => subtreeKeys(child, into));
  return into;
};

const buildTree = (categories: CategoryInfo[]): MenuNode[] => {
  // The API already sorts; just bucket consecutive categories by group.
  const groups: { name: string; categories: CategoryInfo[] }[] = [];
  categories.forEach((cat) => {
    const name = cat.menu_group || 'Other';
    const last = groups[groups.length - 1];
    if (last && last.name === name) {
      last.categories.push(cat);
    } else {
      groups.push({ name, categories: [cat] });
    }
  });

  return groups.map((group) => {
    // Inside a group, consecutive categories sharing a menu_sub ("Driver
    // License" under US) fold into one sub-row; the rest sit directly under
    // the group. Topics are left to the filters on the page.
    const children: MenuNode[] = [];
    group.categories.forEach((cat) => {
      const leaf: MenuNode = {
        key: `c:${cat.name}`,
        label: cat.name,
        params: { category: cat.name },
        children: [],
        questionCount: cat.question_count || 0,
      };
      if (!cat.menu_sub) {
        children.push(leaf);
        return;
      }
      const subKey = `s:${group.name}/${cat.menu_sub}`;
      const last = children[children.length - 1];
      if (last && last.key === subKey) {
        last.children.push(leaf);
        last.questionCount = (last.questionCount || 0) + leaf.questionCount!;
      } else {
        children.push({
          key: subKey,
          label: cat.menu_sub,
          params: { group: group.name, sub: cat.menu_sub },
          children: [leaf],
          questionCount: leaf.questionCount,
        });
      }
    });
    return {
      key: `g:${group.name}`,
      label: group.name,
      params: { group: group.name },
      children,
      questionCount: children.reduce(
        (sum, c) => sum + (c.questionCount || 0),
        0,
      ),
    };
  });
};

const GeneralTestsNav: FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'site_header' });
  const navigate = useNavigate();
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [flyout] = useState(canHover);
  /** Touch: rows expanded in place. */
  const [expanded, setExpanded] = useState<string[]>([]);
  /** Mouse: the chain of open flyouts, outermost first. */
  const [path, setPath] = useState<string[]>([]);

  /* Hovering a row points the open chain at it after a short pause, so the
     pointer cutting across a neighbouring row on its way into a flyout does
     not swap flyouts. Any later hover replaces the pending one. */
  const pathTimer = useRef<ReturnType<typeof setTimeout> | undefined>();
  const cancelPath = () => {
    if (pathTimer.current) {
      clearTimeout(pathTimer.current);
      pathTimer.current = undefined;
    }
  };
  const pointAt = (chain: string[], now: boolean) => {
    cancelPath();
    if (now) {
      setPath(chain);
      return;
    }
    pathTimer.current = setTimeout(() => setPath(chain), 150);
  };
  useEffect(() => cancelPath, []);

  const reset = () => {
    cancelPath();
    setPath([]);
    setExpanded([]);
  };

  const { open, setOpen, hoverProps } = useHoverMenu(reset);

  useEffect(() => {
    listPublicCategories()
      .then((data) => setCategories(data.categories))
      .catch(() => setCategories([]));
  }, []);

  const tree = useMemo(() => buildTree(categories), [categories]);

  const goTo = (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    navigate(`/public-tests${qs ? `?${qs}` : ''}`);
    setOpen(false);
    reset();
  };

  /** Open a branch and close whatever else is open at that same level. */
  const toggle = (node: MenuNode, siblings: MenuNode[]) => {
    setExpanded((prev) => {
      if (prev.includes(node.key)) {
        const closing = subtreeKeys(node);
        return prev.filter((k) => !closing.includes(k));
      }
      const closing = siblings
        .filter((s) => s.key !== node.key)
        .flatMap((s) => subtreeKeys(s));
      return [...prev.filter((k) => !closing.includes(k)), node.key];
    });
  };

  const rowClass = (depth: number) =>
    classnames(
      'd-flex align-items-center justify-content-between categories-nav__row',
      { 'fw-semibold categories-nav__row--top': depth === 0 },
    );

  /** Badge, name, summary and count — the same in both modes. */
  const renderLabel = (node: MenuNode, depth: number) =>
    depth === 0 ? (
      <>
        <span className="d-flex align-items-center">
          <span className="categories-nav__badge">{badgeFor(node.label)}</span>
          <span className="d-flex flex-column">
            <span>{node.label}</span>
            {subtitleFor(node) && (
              <span className="categories-nav__subtitle">
                {subtitleFor(node)}
              </span>
            )}
          </span>
        </span>
        {!!node.questionCount && (
          <span className="categories-nav__count me-2">
            {node.questionCount}
          </span>
        )}
      </>
    ) : (
      <span>{node.label}</span>
    );

  /* A long flyout opened low in the list would run off the bottom of the
     window; lift it just enough to fit. */
  const keepInViewport = (el: HTMLDivElement | null) => {
    if (!el) return;
    const overflow =
      el.getBoundingClientRect().bottom - (window.innerHeight - 8);
    if (overflow > 0) {
      el.style.top = `${parseFloat(getComputedStyle(el).top) - overflow}px`;
    }
  };

  /** Mouse mode: each row's children open in a panel to its right. */
  const renderFlyout = (
    nodes: MenuNode[],
    depth: number,
    ancestors: string[],
  ) =>
    nodes.map((node) => {
      const hasChildren = node.children.length > 0;
      // Hovering a leaf still counts: it closes its siblings' flyouts.
      const chain = hasChildren ? [...ancestors, node.key] : ancestors;
      const isOpen = path[depth] === node.key;
      // A leaf opens no flyout of its own, so the 150ms debounce below (for
      // the pointer cutting across a row on its way past) buys it nothing -
      // it only leaves a window where a real click on the leaf can land
      // between mousedown and mouseup while that delayed setPath re-renders
      // the tree, and the click gets lost. Leaves resolve immediately;
      // branches keep the debounce.
      const pointNow = hasChildren ? isOpen : true;
      return (
        <div
          key={node.key}
          className="categories-nav__item"
          onMouseEnter={() => pointAt(chain, pointNow)}
          // React's onFocus is the bubbling "focusin" event, so focusing a
          // leaf two levels down still reaches this handler on every
          // ancestor row unless it's stopped here — otherwise a leaf's own
          // mousedown (which focuses it right before the click that should
          // navigate) re-triggers a distant ancestor's setPath mid-click,
          // and the browser drops the click when the re-render lands the
          // mouseup on a different node than the mousedown. Only a row's
          // own focus should reopen its flyout.
          onFocus={(e) => {
            e.stopPropagation();
            if (hasChildren) pointAt(chain, true);
          }}>
          <NavDropdown.Item
            className={rowClass(depth)}
            onClick={() => goTo(node.params)}>
            {renderLabel(node, depth)}
            {hasChildren && (
              <Icon name="chevron-right" className="small ms-3 opacity-75" />
            )}
          </NavDropdown.Item>
          {isOpen && (
            <div
              ref={keepInViewport}
              className={classnames(
                'dropdown-menu show categories-nav__flyout',
                {
                  // Only a panel of leaves may scroll: a scrolling panel
                  // would clip the flyouts of its own rows.
                  'categories-nav__flyout--scroll': node.children.every(
                    (c) => !c.children.length,
                  ),
                },
              )}>
              {renderFlyout(node.children, depth + 1, chain)}
            </div>
          )}
        </div>
      );
    });

  /** Touch mode: the chevron expands a row's children in place. */
  const renderAccordion = (nodes: MenuNode[], depth: number) =>
    nodes.map((node) => {
      const hasChildren = node.children.length > 0;
      const isOpen = expanded.includes(node.key);
      return (
        <Fragment key={node.key}>
          <NavDropdown.Item
            className={rowClass(depth)}
            style={{ paddingLeft: `${1 + depth * 1.1}rem` }}
            onClick={() => goTo(node.params)}>
            {renderLabel(node, depth)}
            {hasChildren && (
              <span
                role="button"
                tabIndex={0}
                aria-label={t(isOpen ? 'collapse' : 'expand', {
                  name: node.label,
                })}
                aria-expanded={isOpen}
                className="ms-3 px-2 py-1 opacity-75 categories-nav__caret"
                onClick={(e) => {
                  // The row navigates; only this control opens it.
                  e.preventDefault();
                  e.stopPropagation();
                  toggle(node, nodes);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    toggle(node, nodes);
                  }
                }}>
                <Icon
                  name={isOpen ? 'chevron-down' : 'chevron-right'}
                  className="small"
                />
              </span>
            )}
          </NavDropdown.Item>
          {isOpen && renderAccordion(node.children, depth + 1)}
        </Fragment>
      );
    });

  return (
    <div className="categories-nav__hover" {...hoverProps}>
      <NavDropdown
        id="general-tests-nav"
        className={classnames('categories-nav', {
          'categories-nav--flyout': flyout,
        })}
        show={open}
        onToggle={(next) => {
          setOpen(next);
          if (!next) reset();
        }}
        // Expanding a row must not dismiss the menu.
        autoClose="outside"
        title={
          <>
            <Icon name="list" className="me-2 lh-1" />
            <span>{t('categories')}</span>
          </>
        }>
        {flyout ? renderFlyout(tree, 0, []) : renderAccordion(tree, 0)}
      </NavDropdown>
    </div>
  );
};

export default GeneralTestsNav;
